// src/components/RepoForm.jsx
import React, { useMemo, useState, useEffect } from "react";

const SYSTEMS = [
  { value: "git", label: "Git Repository (GitHub/Azure DevOps/Bitbucket)" },
  { value: "salesforce", label: "Salesforce (SFDC)" },
  { value: "servicenow", label: "ServiceNow" },
  { value: "sap", label: "SAP" },
];

const GIT_PROVIDER_VALUES = ["github", "azure_devops", "bitbucket", "git", "local"];

function Field({ label, children, hint }) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {hint ? <div className="hint">{hint}</div> : null}
    </div>
  );
}

function Grid2({ children }) {
  return <div className="grid-2">{children}</div>;
}

function cleanObj(obj) {
  const out = {};
  Object.keys(obj || {}).forEach((k) => {
    const v = obj[k];
    if (v === undefined || v === null) return;
    if (typeof v === "string" && v.trim() === "") return;
    out[k] = v;
  });
  return out;
}

export default function RepoForm({ onSubmit, submitting, submitLabel = "Save", initial }) {
  const initialSystemType = initial?.provider
    ? (GIT_PROVIDER_VALUES.includes(initial.provider) ? "git" : initial.provider)
    : "git";
  const [systemType, setSystemType] = useState(initialSystemType);

  // Common
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");

  // Provider (git only)
  const [provider, setProvider] = useState(initial?.provider || "github");

  // Git fields
  const [repoUrl, setRepoUrl] = useState(initial?.repo_url || "");
  const [defaultBranch, setDefaultBranch] = useState(initial?.default_branch || "main");

  // Salesforce fields
  const [sfdcLoginUrl, setSfdcLoginUrl] = useState(initial?.settings_json?.SFDC_LOGIN_URL || "https://login.salesforce.com");
  const [sfdcApiVersion, setSfdcApiVersion] = useState(initial?.settings_json?.SFDC_API_VERSION || "60.0");
  const [sfdcUsername, setSfdcUsername] = useState("");
  const [sfdcPassword, setSfdcPassword] = useState("");
  const [sfdcSecurityToken, setSfdcSecurityToken] = useState("");

  // ServiceNow fields
  const [snInstanceUrl, setSnInstanceUrl] = useState(initial?.settings_json?.SN_INSTANCE_URL || "");
  const [snApiVersion, setSnApiVersion] = useState(initial?.settings_json?.SN_API_VERSION || "v1");
  const [snUsername, setSnUsername] = useState("");
  const [snPassword, setSnPassword] = useState("");
  const [snScope, setSnScope] = useState(initial?.settings_json?.SN_SCOPE || "global");

  // SAP fields
  const [sapEndpointType, setSapEndpointType] = useState(initial?.settings_json?.SAP_ENDPOINT_TYPE || "odata");
  const [sapBaseUrl, setSapBaseUrl] = useState(initial?.settings_json?.SAP_BASE_URL || "");
  const [sapClient, setSapClient] = useState(initial?.settings_json?.SAP_CLIENT || "100");
  const [sapAshost, setSapAshost] = useState(initial?.settings_json?.SAP_ASHOST || "");
  const [sapSysnr, setSapSysnr] = useState(initial?.settings_json?.SAP_SYSNR || "");
  const [sapLang, setSapLang] = useState(initial?.settings_json?.SAP_LANG || "EN");
  const [sapRouter, setSapRouter] = useState(initial?.settings_json?.SAP_ROUTER || "");
  const [sapUsername, setSapUsername] = useState("");
  const [sapPassword, setSapPassword] = useState("");

  // keep provider aligned for non-git
  useEffect(() => {
    if (systemType !== "git") setProvider(systemType);
  }, [systemType]);

  const canSubmit = useMemo(() => {
    if (!name.trim()) return false;
    if (systemType === "git") return !!repoUrl.trim();
    if (systemType === "salesforce") return !!sfdcLoginUrl.trim() && !!sfdcApiVersion.trim();
    if (systemType === "servicenow") return !!snInstanceUrl.trim();
    if (systemType === "sap") return !!(sapEndpointType === "odata" ? sapBaseUrl.trim() : (sapAshost.trim() && sapSysnr.trim()));
    return true;
  }, [name, systemType, repoUrl, sfdcLoginUrl, sfdcApiVersion, snInstanceUrl, sapEndpointType, sapBaseUrl, sapAshost, sapSysnr]);

  const submit = (e) => {
    e.preventDefault();

    let settings_json = {};
    let secrets_json = {};
    let finalRepoUrl = null;
    let finalBranch = null;

    if (systemType === "git") {
      finalRepoUrl = repoUrl.trim();
      finalBranch = (defaultBranch || "main").trim();
      settings_json = {};
      secrets_json = {};
    }

    if (systemType === "salesforce") {
      // keep repo_url as login url (safe placeholder even if DB requires NOT NULL)
      finalRepoUrl = sfdcLoginUrl.trim();
      finalBranch = null;

      settings_json = cleanObj({
        SFDC_LOGIN_URL: sfdcLoginUrl,
        SFDC_API_VERSION: sfdcApiVersion,
      });

      secrets_json = cleanObj({
        SFDC_USERNAME: sfdcUsername,
        SFDC_PASSWORD: sfdcPassword,
        SFDC_SECURITY_TOKEN: sfdcSecurityToken,
      });
    }

    if (systemType === "servicenow") {
      finalRepoUrl = snInstanceUrl.trim();
      finalBranch = null;

      settings_json = cleanObj({
        SN_INSTANCE_URL: snInstanceUrl,
        SN_API_VERSION: snApiVersion,
        SN_SCOPE: snScope,
      });

      secrets_json = cleanObj({
        SN_USERNAME: snUsername,
        SN_PASSWORD: snPassword,
      });
    }

    if (systemType === "sap") {
      finalRepoUrl = (sapEndpointType === "odata" ? sapBaseUrl : sapAshost).trim();
      finalBranch = null;

      settings_json = cleanObj({
        SAP_ENDPOINT_TYPE: sapEndpointType,
        SAP_BASE_URL: sapBaseUrl,
        SAP_CLIENT: sapClient,
        SAP_ASHOST: sapAshost,
        SAP_SYSNR: sapSysnr,
        SAP_LANG: sapLang,
        SAP_ROUTER: sapRouter,
      });

      secrets_json = cleanObj({
        SAP_USERNAME: sapUsername,
        SAP_PASSWORD: sapPassword,
      });
    }

    const payload = {
      name: name.trim(),
      provider: systemType === "git" ? provider : systemType,
      repo_url: finalRepoUrl,
      default_branch: finalBranch,
      description: description.trim() || null,
      settings_json,
      secrets_json,
    };

    onSubmit(payload);
  };

  return (
    <form className="form" onSubmit={submit}>
      <Grid2>
        <Field label="Repository / Connection Name">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., BackendRepo1 / SFDC-DevOrg" />
        </Field>

        <Field label="System Type" hint="Choose what you want to scan.">
          <select value={systemType} onChange={(e) => setSystemType(e.target.value)}>
            {SYSTEMS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </Field>
      </Grid2>

      <Field label="Description (optional)">
        <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short notes..." />
      </Field>

      {systemType === "git" && (
        <>
          <Field label="Git Provider">
            <select value={provider} onChange={(e) => setProvider(e.target.value)}>
              <option value="github">github</option>
              <option value="azure_devops">azure_devops</option>
              <option value="bitbucket">bitbucket</option>
              <option value="git">git</option>
              <option value="local">local</option>
            </select>
          </Field>

          <Field label="Repo URL">
            <input value={repoUrl} onChange={(e) => setRepoUrl(e.target.value)} placeholder="https://github.com/org/repo.git" />
          </Field>

          <Field label="Default Branch">
            <input value={defaultBranch} onChange={(e) => setDefaultBranch(e.target.value)} placeholder="main" />
          </Field>
        </>
      )}

      {systemType === "salesforce" && (
        <>
          <Grid2>
            <Field label="SFDC Login URL" hint="https://login.salesforce.com (prod) or https://test.salesforce.com (sandbox)">
              <input value={sfdcLoginUrl} onChange={(e) => setSfdcLoginUrl(e.target.value)} />
            </Field>
            <Field label="SFDC API Version">
              <input value={sfdcApiVersion} onChange={(e) => setSfdcApiVersion(e.target.value)} placeholder="60.0" />
            </Field>
          </Grid2>

          <Grid2>
            <Field label="SFDC Username">
              <input value={sfdcUsername} onChange={(e) => setSfdcUsername(e.target.value)} placeholder="user@company.com" />
            </Field>
            <Field label="SFDC Password">
              <input type="password" value={sfdcPassword} onChange={(e) => setSfdcPassword(e.target.value)} />
            </Field>
          </Grid2>

          <Field label="SFDC Security Token" hint="If IP not whitelisted, token required">
            <input type="password" value={sfdcSecurityToken} onChange={(e) => setSfdcSecurityToken(e.target.value)} />
          </Field>
        </>
      )}

      {systemType === "servicenow" && (
        <>
          <Grid2>
            <Field label="Instance URL" hint="https://dev12345.service-now.com">
              <input value={snInstanceUrl} onChange={(e) => setSnInstanceUrl(e.target.value)} />
            </Field>
            <Field label="API Version">
              <input value={snApiVersion} onChange={(e) => setSnApiVersion(e.target.value)} placeholder="v1" />
            </Field>
          </Grid2>

          <Field label="Scope">
            <input value={snScope} onChange={(e) => setSnScope(e.target.value)} placeholder="global" />
          </Field>

          <Grid2>
            <Field label="Username">
              <input value={snUsername} onChange={(e) => setSnUsername(e.target.value)} />
            </Field>
            <Field label="Password">
              <input type="password" value={snPassword} onChange={(e) => setSnPassword(e.target.value)} />
            </Field>
          </Grid2>
        </>
      )}

      {systemType === "sap" && (
        <>
          <Field label="Endpoint Type">
            <select value={sapEndpointType} onChange={(e) => setSapEndpointType(e.target.value)}>
              <option value="odata">OData / HTTP API</option>
              <option value="rfc">RFC (ASHOST/SYSNR)</option>
            </select>
          </Field>

          {sapEndpointType === "odata" ? (
            <Field label="SAP Base URL">
              <input value={sapBaseUrl} onChange={(e) => setSapBaseUrl(e.target.value)} placeholder="https://sap-gw.../sap/opu/odata/..." />
            </Field>
          ) : (
            <Grid2>
              <Field label="ASHOST">
                <input value={sapAshost} onChange={(e) => setSapAshost(e.target.value)} placeholder="host/ip" />
              </Field>
              <Field label="SYSNR">
                <input value={sapSysnr} onChange={(e) => setSapSysnr(e.target.value)} placeholder="00" />
              </Field>
            </Grid2>
          )}

          <Grid2>
            <Field label="Client">
              <input value={sapClient} onChange={(e) => setSapClient(e.target.value)} />
            </Field>
            <Field label="Language">
              <input value={sapLang} onChange={(e) => setSapLang(e.target.value)} />
            </Field>
          </Grid2>

          <Field label="Router (optional)">
            <input value={sapRouter} onChange={(e) => setSapRouter(e.target.value)} />
          </Field>

          <Grid2>
            <Field label="Username">
              <input value={sapUsername} onChange={(e) => setSapUsername(e.target.value)} />
            </Field>
            <Field label="Password">
              <input type="password" value={sapPassword} onChange={(e) => setSapPassword(e.target.value)} />
            </Field>
          </Grid2>
        </>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12 }}>
        <button className="btn primary" disabled={!canSubmit || submitting} type="submit">
          {submitting ? "Saving…" : submitLabel}
        </button>
      </div>
    </form>
  );
}
